/**
 * @file import-po-excel.tsx
 * @module app/modals/import-po-excel
 * @description Renders the import po excel modal dialog for confirming actions, capturing workflow input, and reporting outcomes to its parent screen.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import React, { useEffect, useState } from "react";
import { Modal, Button, FormLabel, Row, Col } from "react-bootstrap";
import APICallService from "../../api/apiCallService";
import { PURCHASEORDER } from "../../api/apiEndPoints";
import { success, error } from "../../Global/toast";
import Method from "../../utils/methods";
import cartoonIcon from "../../_admin/assets/media/product/cartoon.png";


type Props = {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
};

const ImportPOModal = ({ show, onHide, onSuccess }: Props) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [validationData, setValidationData] = useState<any>(null);

  useEffect(() => {
    if (show) {
      setFile(null);
      setLoading(false);
      setValidationData(null);
    }
  }, [show]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const allowedExtensions = ["xlsx", "xls"];
      const allowedMimeTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel"
      ];
      const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();

      const isValidExtension = allowedExtensions.includes(fileExtension || "");
      // If the browser provides a type, it must be an Excel MIME type
      const isValidMimeType = !selectedFile.type || allowedMimeTypes.includes(selectedFile.type);

      if (!isValidExtension || !isValidMimeType) {
        error("Invalid file format. Please upload only Excel files (.xlsx, .xls)");
        if (e.target) {
          e.target.value = ''; // clear input
        }
        setFile(null);
        return;
      }
      setFile(selectedFile);
    }
  };
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadSample = async () => {
    setIsDownloading(true);
    try {
      const apiService = new APICallService(
        PURCHASEORDER.downloadSample,
        undefined,
        undefined,
        "arraybuffer"
      );
      const response = await apiService.callAPI();
      if (response) {
        const blob = new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "po_sample.xlsx";
        link.click();
        URL.revokeObjectURL(url);
      } else {
        error("Failed to download sample file");
      }
    } catch (err) {
      error("Failed to download sample file");
    } finally {
      setIsDownloading(false);
    }
  };
  const handleSubmit = async () => {
    if (!file) {
      error("Please select an Excel file to upload");
      return;
    }

    setLoading(true);
    try {
      const params = new FormData();
      params.append("file", file);

      const apiService = new APICallService(PURCHASEORDER.bulkAddValidations, params, "", "application/json", "en", true);
      const response = await apiService.callAPI();

      let parsedResponse = response;
      if (typeof response === "string") {
        try {
          parsedResponse = JSON.parse(response);
        } catch (e) {
          console.error("Failed to parse response JSON", e);
        }
      }

      console.log("RAW VALIDATION RESPONSE:", parsedResponse);

      if (parsedResponse?.error) {
        error(parsedResponse.error);
        setLoading(false);
        return;
      }

      if (!parsedResponse || parsedResponse === 0) {
        setLoading(false);
        return;
      }

      const vendors = parsedResponse?.nonExistingVendors || [];
      const variants = parsedResponse?.nonExistingProductVariantsName || [];
      const notMatchedQtyWithProduct = parsedResponse?.notMatchedQtyWithProduct || [];
      const nonExistingWarehouse = parsedResponse?.nonExistingWarehouse || [];
      let payloadArr = parsedResponse?.bulkPayload || [];

      if (typeof payloadArr === "string") {
        try {
          payloadArr = JSON.parse(payloadArr);
        } catch (e) {
          console.error("Failed to parse bulkPayload string", e);
          payloadArr = [];
        }
      }

      const hasErrors = vendors.length > 0 || variants.length > 0 || notMatchedQtyWithProduct.length > 0 || nonExistingWarehouse.length > 0;

      if (hasErrors) {
        setValidationData({
          nonExistingVendors: vendors,
          nonExistingProductVariantsName: variants,
          notMatchedQtyWithProduct: notMatchedQtyWithProduct,
          nonExistingWarehouse: nonExistingWarehouse,
          bulkPayload: payloadArr
        });
      } else {
        // Success case - call bulk-add
        const bulkPayloadArray = Array.isArray(payloadArr) ? payloadArr : [];

        if (bulkPayloadArray.length === 0) {
          error("Bulk payload mapping unresolved or empty.");
          setLoading(false);
          return;
        }

        console.log("SENDING BULK ADD PAYLOAD:", { bulkPayload: bulkPayloadArray });
        const bulkAddService = new APICallService(PURCHASEORDER.bulkAdd, { bulkPayload: bulkPayloadArray }, "","","",true);
        const bulkAddRes = await bulkAddService.callAPI();
        
        let parsedBulkResponse = bulkAddRes;
        if (typeof bulkAddRes === "string") {
          try {
            parsedBulkResponse = JSON.parse(bulkAddRes);
          } catch (e) {
            console.error("Failed to parse response JSON", e);
          }
        }

        if (parsedBulkResponse?.error) {
          error(parsedBulkResponse.error);
          setLoading(false);
          return;
        }

        if (!parsedBulkResponse || parsedBulkResponse === 0) {
          setLoading(false);
          return;
        }

        success("PO data added successfully!");
        onSuccess();
        onHide();
      }
    } catch (err) {
      error("Failed to import Purchase Orders");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal show={show && !validationData} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title>Import PO from Excel</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4">
          <Row>
            <Col className="mb-3">
              <div className="d-flex align-items-center justify-content-end mb-4">
                <Button className="uw-btn uw-btn-primary uw-btn-sm"
                  variant="primary"
                  size="sm"
                  onClick={handleDownloadSample}
                  disabled={loading || isDownloading}
                >
                  {isDownloading ? "Downloading..." : "Download Sample File"}
                </Button>
              </div>
              <input
                type="file"
                className="form-control"
                accept=".xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                onChange={handleFileChange}
                disabled={loading}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button className="uw-btn uw-btn-danger" variant="danger" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button className="uw-btn uw-btn-primary" variant="primary" onClick={handleSubmit} disabled={loading || !file}>
            {loading ? "Loading..." : "Import"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Validation Error Modal */}
      <Modal
        show={!!validationData}
        onHide={() => {
          setValidationData(null);
          onHide();
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-0" >
          <p
            className="fw-bold text-center mb-0"
            style={{ color: '#2B3674', letterSpacing: '0.3px', fontSize: "16px" }}
          >
            Import Validation Summary
          </p>
        </Modal.Header>

        <Modal.Body className="px-4 pt-2 pb-4">

          {/* Title */}


          {/* Alert Message */}
          <div
            className="rounded mb-4 p-3 bg-danger"
            style={{
              fontSize: '16px',
              lineHeight: '1.6',
              color: "white"
            }}
          >
            We couldn’t match some items in your file with existing records. <br />
            To continue, please review and match the following:
          </div>

          {/* Content Section */}
          <div
            className="rounded p-4"
            style={{ backgroundColor: '#F9FAFB' }}
          >

            {/* Vendors */}
            {validationData?.nonExistingVendors?.length > 0 && (
              <div className="mb-4">
                <div
                  className="fw-semibold mb-2 d-flex align-items-center gap-2"
                  style={{ color: '#2B3674', fontSize: '16px' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                  Unmatched Vendor ({validationData.nonExistingVendors.length})
                </div>

                <ul className="mb-0 ps-9">
                  {validationData.nonExistingVendors.map((vendor: any, index: number) => (
                    <li key={index} className="mb-1" style={{ fontSize: '16px' }}>
                      <span className="text-danger">{vendor}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Products */}
            {validationData?.nonExistingProductVariantsName?.length > 0 && (
              <div className="mb-4">
                <div
                  className="fw-semibold mb-2 d-flex align-items-center gap-2"
                  style={{ color: '#2B3674', fontSize: '16px' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>
                  Unmatched Products ({validationData.nonExistingProductVariantsName.length})
                </div>

                <ul className="mb-0 ps-9">
                  {validationData.nonExistingProductVariantsName.map((product: any, index: number) => (
                    <li key={index} className="mb-1" style={{ fontSize: '16px' }}>
                      <span className="text-danger">{product}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Quantity Mismatch */}
            {validationData?.notMatchedQtyWithProduct?.length > 0 && (
              <div className="mb-4">
                <div
                  className="fw-semibold mb-3 d-flex align-items-center gap-2"
                  style={{ color: '#2B3674', fontSize: '16px' }}
                >
                  <img src={cartoonIcon} alt="Quantity Icon" width="20" height="20" />
                  Unmatched Quantity Types With Product ({validationData.notMatchedQtyWithProduct.length})
                </div>

                <ul className="mb-0 ps-9">
                  {validationData.notMatchedQtyWithProduct.map(
                    (productData: any, index: number) => (
                      <li key={index} className="mb-1" style={{ fontSize: '16px' }}>
                        <span className="text-danger">
                          {productData.name}
                          {productData.quantityTypes?.length > 0 && " - "}
                          {productData.quantityTypes?.map((qty: any) =>
                            Method.formatQuantityDisplay(qty.type, qty.stockCount)
                          ).join(" | ")}
                        </span>
                      </li>
                    )
                  )}
                </ul>
              </div>
            )}

            {/* Warehouses */}
            {validationData?.nonExistingWarehouse?.length > 0 && (
              <div>
                <div
                  className="fw-semibold mb-2 d-flex align-items-center gap-2"
                  style={{ color: '#2B3674', fontSize: '16px' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
                  Unmatched Warehouse ({validationData.nonExistingWarehouse.length})
                </div>

                <ul className="mb-0 ps-9">
                  {validationData.nonExistingWarehouse.map((warehouse: any, index: number) => (
                    <li key={index} className="mb-1" style={{ fontSize: '16px' }}>
                      <span className="text-danger">{warehouse}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

          </div>

        </Modal.Body>
      </Modal>
    </>
  );
};

export default ImportPOModal;
